import { z } from 'zod';

/**
 * Order summary aligned with backend response
 */
export interface OrderSummary {
  quantity: number;
  pricePerCredit: number;
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  total: number;
  currency: string;
}

/**
 * Zod schema for the checkout / billing form
 * Billing address fields are conditionally required based on `useDifferentBillingAddress`.
 * When false (using personal address), address fields are optional.
 * When true (or no profile address exists), address fields are required.
 */
export const checkoutSchema = z
  .object({
    // User address fields (editable, shown in customer data section)
    userOrganization: z.string().or(z.literal('')),
    userEmail: z.string().min(1).email().or(z.literal('')),
    userStreet: z.string().optional().or(z.literal('')),
    userHouseNumber: z.string().optional().or(z.literal('')),
    userHouseNumberAddition: z.string().optional().or(z.literal('')),
    userCity: z.string().optional().or(z.literal('')),
    userPostalCode: z.string().optional().or(z.literal('')),
    userCountry: z.string().min(1),
    // Billing address fields (shown when "Abweichende Lieferstelle" is checked)
    billingFirstName: z.string().optional().or(z.literal('')),
    billingLastName: z.string().optional().or(z.literal('')),
    billingEmail: z.string().min(1).email().or(z.literal('')),
    billingOrganization: z.string().or(z.literal('')),
    billingStreet: z.string().optional().or(z.literal('')),
    billingHouseNumber: z.string().optional().or(z.literal('')),
    billingHouseNumberAddition: z.string().optional().or(z.literal('')),
    billingCity: z.string().optional().or(z.literal('')),
    billingPostalCode: z.string().optional().or(z.literal('')),
    billingCountry: z.string().optional().or(z.literal('')),
    vatNumber: z.string().optional().or(z.literal('')),
    useDifferentBillingAddress: z.boolean(),
    acceptTerms: z.literal(true, {
      error: 'acceptTermsRequired',
    }),
  })
  .superRefine((data, ctx) => {
    // When using a different billing address, require address fields
    if (data.useDifferentBillingAddress) {
      const requiredFields = [
        'billingEmail',
        'billingOrganization',
        'billingStreet',
        'billingHouseNumber',
        'billingCity',
        'billingPostalCode',
        'billingCountry',
      ] as const;

      for (const field of requiredFields) {
        if (!data[field] || data[field].length === 0) {
          ctx.addIssue({
            code: 'custom',
            path: [field],
            message: 'Required',
          });
        }
      }
    }
  });

export type CheckoutFormData = z.infer<typeof checkoutSchema>;
